// One player's stored handicap entry, captured at registration/entry — now
// with a SOURCE flag so the director can later see who is GHIN-verified vs
// self-reported, and every downstream game / net-scoring path reads a single
// consistent shape.
//
//   source:     'ghin' — the index came from a SUCCESSFUL GHIN lookup
//               'self' — the index was typed in by hand (self-reported)
//               null   — no index entered at all
//   verifiedAt: timestamp when source === 'ghin', else null
//
// Pure (no React/Firebase) so it's unit-testable under `node --test`. Reuses
// the existing positional handicap.js formulas (unchanged).
import { calcCourseHandicap, calcPlayingHandicap, normalizeGhin } from './handicap.js'

// ── Self-reported allow/disallow (STEP 2) ─────────────────────────────────
// Whether a hand-typed (self-reported) index is accepted, or only a
// GHIN-verified one. Default derives from handicapMode: `required` = GHIN
// only (self-reported blocked); anything else allows self-reporting. The
// admin can override via info.allowSelfReported.
export function allowSelfReportedDefault(handicapMode) {
  return handicapMode !== 'required'
}

export function resolveAllowSelfReported(info) {
  const v = info?.allowSelfReported
  if (v === true || v === false) return v
  return allowSelfReportedDefault(info?.handicapMode || 'none')
}

// True when an entry must be rejected because self-reported handicaps aren't
// allowed for this event (i.e. it isn't GHIN-verified).
export function isSelfReportedBlocked(source, allowSelfReported) {
  if (allowSelfReported) return false
  return source !== 'ghin'
}

export function buildHandicapEntry({
  ghin, index, source, slopeRating, courseRating, coursePar, allowance, now,
}) {
  const n = index === '' || index == null ? null : Number(index)
  const idx = Number.isFinite(n) ? n : null
  const ch = idx == null ? null : calcCourseHandicap(idx, slopeRating, courseRating, coursePar)
  const ph = ch == null ? null : calcPlayingHandicap(ch, allowance)
  // Only stamp a source when there's actually an index. A GHIN number alone
  // (no resolved index) is not "verified".
  const src = idx == null ? null : (source === 'ghin' ? 'ghin' : 'self')
  return {
    ghin: normalizeGhin(ghin) || null,
    handicapIndex: idx,
    courseHandicap: ch,
    playingHandicap: ph,
    source: src,
    verifiedAt: src === 'ghin' ? (Number.isFinite(now) ? now : Date.now()) : null,
  }
}

// ── STEP 5: registration handicap review + required-mode net gate ──────────
// Shared name normalizer — MUST match data.js snapshotNetHandicaps' read side
// so the name→index map keys line up (trim + lowercase).
const normName = (s) => String(s || '').trim().toLowerCase()

// A handicap index is present only when it's a real finite number. null /
// undefined / '' all mean "no index entered" (note: Number(null) === 0, so a
// loose Number() check would wrongly treat a null index as scratch). In real
// Firebase data null keys are stripped to undefined, so this matches the
// snapshot's original behavior on stored data while also handling in-memory
// nulls consistently.
const finiteIndex = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

// Build the name → handicap-index map the net snapshot feeds on, from every
// registration's parallel players/playerHandicaps. When self-reported indexes
// aren't allowed (GHIN-required events), non-'ghin' sources are skipped so they
// never feed net. Pure — no Firebase.
export function buildNameToIndex(registrations, allowSelfReported) {
  const map = {}
  for (const reg of Object.values(registrations || {})) {
    const names = reg?.players || []
    const phs = reg?.playerHandicaps || []
    names.forEach((nm, i) => {
      const entry = phs?.[i]
      const idx = finiteIndex(entry?.handicapIndex)
      if (idx === null) return
      // Required-mode: only GHIN-verified indexes feed net.
      if (!allowSelfReported && entry?.source !== 'ghin') return
      map[normName(nm)] = idx
    })
  }
  return map
}

// Flatten registrations into a per-player review list + source counts, for the
// admin "who self-reported" view. Players with an index that isn't GHIN-verified
// read as 'self'; players with no parallel index read as source null ('none').
// Pure — no Firebase/React.
export function collectHandicapReview(registrations) {
  const rows = []
  const counts = { ghin: 0, self: 0, none: 0, total: 0 }
  for (const [regId, reg] of Object.entries(registrations || {})) {
    const names = reg?.players || []
    const phs = reg?.playerHandicaps || []
    const teamName = reg?.teamName || ''
    names.forEach((nm, i) => {
      const entry = phs?.[i]
      const idx = finiteIndex(entry?.handicapIndex)
      const source = idx !== null ? (entry?.source === 'ghin' ? 'ghin' : 'self') : null
      const allowance = Number.isFinite(Number(entry?.allowance)) ? Number(entry.allowance) : null
      rows.push({
        regId,
        playerIndex: i,
        name: nm,
        teamName,
        handicapIndex: idx,
        source,
        allowance,
        verifiedAt: entry?.verifiedAt ?? null,
      })
      counts.total++
      if (source === 'ghin') counts.ghin++
      else if (source === 'self') counts.self++
      else counts.none++
    })
  }
  return { rows, counts }
}

// ── ALLOWANCE STEP: per-player allowance % override ────────────────────────
// Build name → per-player allowance-override %, from every registration's
// playerHandicaps[i].allowance. Only finite overrides are included; a player
// with no override is absent (the snapshot then falls back to the event
// default). Pure — mirrors buildNameToIndex.
export function buildNameToAllowance(registrations) {
  const map = {}
  for (const reg of Object.values(registrations || {})) {
    const names = reg?.players || []
    const phs = reg?.playerHandicaps || []
    names.forEach((nm, i) => {
      const raw = phs?.[i]?.allowance
      if (raw === null || raw === undefined || raw === '') return
      const a = Number(raw)
      if (Number.isFinite(a)) map[normName(nm)] = a
    })
  }
  return map
}

// A player's effective allowance: their override when it's a finite number,
// otherwise the event default. Pure.
export function effectiveAllowance(override, eventAllowance) {
  if (override === null || override === undefined || override === '') return eventAllowance
  const n = Number(override)
  return Number.isFinite(n) ? n : eventAllowance
}
